/**
 * Substitutes `{{ variable_name }}` placeholders in an HTML template with the
 * supplied values. Used by motion text seeding (api-app) and motion/vote card
 * rendering (apps/web) so the two paths stay in lockstep.
 *
 * Behavior:
 * - Values are HTML-escaped before insertion by default. The template itself
 *   is preserved verbatim, so callers can author HTML markup around the
 *   placeholders and still safely interpolate plain-text user input (item
 *   titles, member names) without breaking the resulting markup.
 * - Keys listed in `rawHtmlKeys` are inserted without escaping — use this for
 *   fields that already contain rich HTML (e.g. `item_summary`).
 * - Unknown variable keys are left in place (e.g. `{{ unknown_var }}`) so
 *   admins can spot typos in their templates after the fact rather than
 *   silently rendering an empty string.
 * - Whitespace inside the braces is tolerated: `{{ name }}` and `{{name}}`
 *   resolve identically.
 *
 * Note: this helper is intentionally minimal — no helpers, conditionals, or
 * dot-paths. If we ever need those, we can swap the implementation here
 * without touching call sites.
 */
const VARIABLE_PATTERN = /\{\{\s*([a-z_]+)\s*\}\}/gi;
const HTML_ESCAPE_MAP = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};
export const escapeHtml = (input) => input.replace(/[&<>"']/g, (char) => HTML_ESCAPE_MAP[char] ?? char);
/**
 * Renders an HTML template with `{{ variable_name }}` placeholders replaced
 * by values from `variables`. Unknown placeholders are left unchanged. See
 * module docs for the full contract.
 *
 * @param template - The HTML template containing `{{var}}` placeholders.
 * @param variables - A map of variable names to their string values. `null`
 *   and `undefined` values are treated as missing (placeholder left intact).
 * @param options - Optional configuration for raw HTML keys.
 * @returns The rendered HTML string.
 */
export const renderHtmlTemplate = (template, variables, options) => {
    const rawHtmlKeys = new Set(options?.rawHtmlKeys);
    return template.replace(VARIABLE_PATTERN, (match, key) => {
        const value = variables[key];
        if (value == null) {
            return match;
        }
        return rawHtmlKeys.has(key) ? value : escapeHtml(value);
    });
};
